import { useEffect, useState } from "react";
import { Lock, CalendarDays, MapPin, Mail, Check, Trophy, Gift, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { media } from "@/components/public/Blocks";
import { AdCard, styleFor } from "@/components/AdSlot";
import MockPage, { SAMPLE_AD } from "@/pages/admin/regie/MockPage";
import { PRESETS } from "@/components/LandingExplain";
import { EXAMPLES } from "@/pages/BrandedLeagues";
import { MARKET_LABEL } from "@/lib/market";

export const MAIL = "hello@mapetiteligue.com";
export const mailto = (subject) => `mailto:${MAIL}?subject=${encodeURIComponent(subject)}`;

let siteCache = null;
const loadSite = () => siteCache || (siteCache = Promise.all([
  api.get("/public/site-demos").then((r) => r.data).catch(() => ({})),
  api.get("/public/showcase").then((r) => r.data).catch(() => []),
]).then(([demos, showcase]) => ({ demos, showcase })));

// Ligues démo réglées dans l'admin pour la catégorie, puis vitrines, puis exemples.
export function useSiteLeagues(cat, order = [0, 1, 2, 3], fallback = null) {
  const [d, setD] = useState(null);
  useEffect(() => { loadSite().then(setD); }, []);
  if (!d) return null;
  const demos = d.demos?.[cat] || [];
  return cat === "play" ? [...demos, ...(fallback || [])] : [...demos, ...d.showcase.filter((s) => !demos.some((x) => x.id === s.id)), ...order.map((i) => EXAMPLES[i])];
}

export function useLogos() {
  const [d, setD] = useState(null);
  useEffect(() => { loadSite().then(setD); }, []);
  if (!d) return [];
  const demoIds = new Set(Object.values(d.demos || {}).flat().map((x) => x.id));
  return d.showcase.filter((s) => s.brand?.logo_url && !demoIds.has(s.id)).map((s) => ({ id: s.id, name: s.name, logo: s.brand.logo_url }));
}

let partnerCache = null;
export function usePartnerDemo() {
  const [d, setD] = useState(undefined);
  useEffect(() => { (partnerCache || (partnerCache = api.get("/site/partner-demo").then((r) => r.data).catch(() => null))).then(setD); }, []);
  return d;
}

const FORMAT_OF = { card: ["card"], banner: ["banner", "card"], mention: ["mention", "text", "card"] };
function demoAd(d, fmt, code) {
  if (!d) return { ...AD, ...(fmt === "banner" ? { layout: { ...AD.layout, tint: "fill" } } : {}) };
  const cr = FORMAT_OF[fmt].map((f) => d.creatives.find((c) => c.format === f)).find(Boolean);
  const base = cr ? { ...cr, offer: null } : { ...SAMPLE_AD };
  return { ...styleFor({ ...base, partner: d.name, partner_color: d.brand_color, partner_logo_url: d.logo_url }, code), preview: true, token: null };
}

export function HeroVisual({ block, fallback, caption, testId }) {
  const img = media(block?.media_url);
  const items = block?.bullets?.length ? block.bullets : caption;
  return (
    <div className="flex w-full min-w-0 flex-col items-center gap-5" data-testid={testId}>
      {img ? <img src={img} alt={block?.title || "Aperçu"} className="max-h-[600px] w-full rounded-sm object-contain" data-testid={`${testId}-img`} /> : fallback}
      <div className="w-full max-w-md">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] p-t3"><span className="h-2 w-2 rounded-full bg-[#B7F52A]" /> {block?.title || "Aperçu"} · {img ? "visuel réel" : "données fictives"}</p>
        {block?.subtitle && <p className="mt-2 text-sm p-t2">{block.subtitle}</p>}
        {items?.length > 0 && <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">{items.map((t) => <li key={t} className="flex items-start gap-2 text-sm"><Check size={14} className="mt-0.5 shrink-0" /> {t}</li>)}</ul>}
      </div>
    </div>
  );
}

export function ScoringPreview() {
  return (
    <div className="p-surface p-5" data-testid="preview-scoring">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] p-t3">Création de la ligue · Barème</p>
      <div className="mt-3 grid grid-cols-3 gap-2">{Object.entries(PRESETS).map(([k, p], i) => (
        <div key={k} className={`rounded-sm border p-3 ${i === 1 ? "border-[#B7F52A] bg-[#B7F52A] text-[#080C0D]" : "p-line"}`}>
          <p className="text-sm font-bold uppercase">{p.label}</p>
          <p className="mt-2 text-xs">Bon prono <b>+{p.ok}</b></p>
          <p className="text-xs">Raté <b>{p.ko || 0}</b></p>
          <p className="text-xs">Série <b>+{p.bonus[0][1]}</b></p>
        </div>))}</div>
      <p className="mt-3 text-xs p-t2">{PRESETS.competition.extra}</p>
      <p className="mt-3 flex items-center gap-2 rounded-sm border p-line px-3 py-2 text-xs font-bold"><Lock size={13} /> Règles figées dès le début de la compétition</p>
    </div>
  );
}

const TEAMS = [["Ventes", 86], ["Tech", 74], ["Marketing", 69], ["Support", 52]];
export function ReportPreview() {
  return (
    <div className="p-surface p-5" data-testid="preview-report">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] p-t3">Rapport mensuel d'engagement · exemple</p>
      <div className="mt-3 grid grid-cols-3 gap-2">{[["78 %", "participation"], ["64", "membres actifs"], ["412", "pronos / semaine"]].map(([v, l]) => (
        <div key={l} className="rounded-sm border p-line p-3"><p className="text-2xl font-bold">{v}</p><p className="text-[11px] p-t3">{l}</p></div>))}</div>
      <p className="mt-4 text-xs font-bold">Participation par équipe</p>
      <ul className="mt-2 space-y-2">{TEAMS.map(([t, v]) => (
        <li key={t} className="flex items-center gap-3 text-xs"><span className="w-20 shrink-0">{t}</span><span className="h-2 flex-1 rounded-full bg-[var(--p-raised)]"><span className="block h-full rounded-full bg-[#B7F52A]" style={{ width: `${v}%` }} /></span><b className="w-9 text-right">{v} %</b></li>))}</ul>
      <p className="mt-3 text-[11px] p-t3">Chiffres agrégés uniquement, jamais de suivi individuel. Données fictives.</p>
    </div>
  );
}

const AD = { preview: true, label: "Offre partenaire", partner: "Ta marque", title: "-15 % sur le maillot de ton équipe", subtitle: "En boutique et en ligne", body: "Offre réservée aux joueurs de la ligue.",
  cta: "Voir l'offre", code: "LIGUE15", partner_color: "#E4572E", layout: { align: "left", text_size: "md", tint: "border" }, offer: { conditions: "Une utilisation par joueur" } };

export function AdFormatsPreview() {
  const d = usePartnerDemo();
  if (d === undefined) return <div className="h-96 w-full max-w-md" />;
  return (
    <div className="grid w-full min-w-0 max-w-md grid-cols-[minmax(0,1fr)] gap-3" data-testid="preview-ad-formats" data-demo={d ? "1" : "0"}>
      {d && <p className="flex items-center gap-2 text-xs font-bold">{d.logo_url && <img src={media(d.logo_url)} alt="" className="h-6 w-6 rounded bg-white object-contain" />} Exemple avec {d.name}</p>}
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] p-t3">Carte · accueil d'une ligue</p>
      <AdCard ad={demoAd(d, "card", "league_home")} />
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] p-t3">Bandeau compact · sous le classement</p>
      <AdCard ad={demoAd(d, "banner", "below_standings")} compact />
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] p-t3">Mention · fil d'activité</p>
      <AdCard ad={demoAd(d, "mention", "sponsor_mention")} mention />
    </div>
  );
}

const DEFAULT_PLACES = [["home", "Accueil général", "card"], ["league_home", "Accueil d'une ligue", "card"], ["below_standings", "Sous le classement", "banner"], ["challenges", "Page des défis", "banner"],
  ["offers_page", "Page des offres partenaires", "card"], ["banner", "Bannière horizontale", "banner"], ["promo_card", "Carte promotionnelle", "card"], ["sponsor_mention", "Mention partenaire", "mention"], ["weekly_recap", "Récapitulatif hebdomadaire", "banner"]]
  .map(([code, name, format]) => ({ code, name, format, where: "" }));

export function PlacementsGallery() {
  const d = usePartnerDemo();
  const places = d?.placements || DEFAULT_PLACES;
  return (
    <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="partners-placements">{places.map((pl) => (
      <figure key={pl.code} className="mpl-device" data-testid={`partners-placement-${pl.code}`}>
        <div className="pointer-events-none"><MockPage placement={{ ...pl, compact: pl.format === "banner" }} ad={demoAd(d, pl.format === "mention" ? "mention" : pl.format === "banner" ? "banner" : "card", pl.code)} brand={null} leagueName="Ligue des potes" /></div>
        <figcaption className="mt-3 text-center"><b className="block text-sm">{pl.name}</b>{pl.where && <span className="text-xs p-t3">{pl.where}</span>}</figcaption>
      </figure>))}</div>
  );
}

export function DemoExperience({ fallback }) {
  const [e, setE] = useState(undefined);
  useEffect(() => { api.get("/site/experience-demo").then(({ data }) => setE(data)).catch(() => setE(null)); }, []);
  if (e === undefined) return <div className="h-80 w-full max-w-md" />;
  if (!e) return fallback;
  const sponsors = e.sponsors || [];
  return (
    <div className="p-surface w-full max-w-lg overflow-hidden shadow-2xl" data-testid="preview-experience-demo">
      <div className="p-photo relative aspect-[4/3]" style={{ backgroundImage: e.hero_url ? `linear-gradient(180deg, rgba(5,9,12,0.1) 30%, rgba(5,9,12,0.92)), url(${media(e.hero_url)})` : "none", backgroundColor: "#05090C" }}>
        <div className="flex h-full flex-col justify-between p-6">
          <div className="flex items-start justify-between gap-2"><span className="p-chip">Exemple · démo</span>{e.winners_count > 0 && <span className="rounded-full bg-[#B7F52A] px-3 py-1 text-xs font-bold text-[#080C0D]">{e.winners_count} gagnant{e.winners_count > 1 ? "s" : ""}</span>}</div>
          <div>
            {e.subtitle && <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#FFFDF5]/80">{e.subtitle}</p>}
            <p className="mt-1 font-display text-6xl uppercase leading-[0.9]" style={{ color: e.brand_color || "#FFFDF5" }}>{e.title || e.name}</p>
          </div>
        </div>
      </div>
      <div className="space-y-3 p-6 text-sm">
        {e.prize_description && <p className="flex items-start gap-2 font-semibold"><Gift size={16} className="mt-0.5 shrink-0" /> {e.prize_description}</p>}
        {e.short_description && <p className="p-t2">{e.short_description}</p>}
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 p-t3">
          <span className="flex items-center gap-1.5"><CalendarDays size={14} /> {e.experience_dates || "Dates à venir"}</span>
          <span className="flex items-center gap-1.5"><MapPin size={14} /> {MARKET_LABEL[e.market] || e.market}</span>
          <span className="flex items-center gap-1.5"><Trophy size={14} /> Classement dédié</span>
        </div>
        {sponsors.length > 0 && <div className="flex flex-wrap items-center gap-3 border-t p-line pt-3"><span className="text-[11px] font-bold uppercase tracking-[0.14em] p-t3">Présentée par</span>{sponsors.map((s) => (
          <span key={s.name} className="flex items-center gap-1.5 font-bold">{s.logo_url && <img src={media(s.logo_url)} alt="" className="h-7 w-7 rounded bg-white object-contain p-0.5" />}{s.name}</span>))}</div>}
        <a href="#liste" className="p-btn w-full justify-center" data-testid="preview-experience-cta">Voir les Experiences <ArrowRight size={15} /></a>
      </div>
    </div>
  );
}

const ROWS = [
  ["Pour qui", "Médias, créateurs, podcasts, fan clubs, associations", "Entreprises, équipes, services, filiales"],
  ["Objectif", "Faire grandir et fidéliser ton audience", "Rassembler et motiver vos collaborateurs"],
  ["Accès", "Public, sur invitation, par code ou par lien", "Fermé : collaborateurs invités uniquement"],
  ["Visibilité", "Page publique de la communauté", "Espace interne, menu dédié, rien de grand public"],
  ["Partenaires", "Aucun, les tiens ou ceux de Ma Petite Ligue", "Aucune publicité externe par défaut"],
  ["Lots", "Lots offerts à ta communauté", "Lots internes de l'entreprise"],
  ["Suivi", "Statistiques agrégées d'audience", "Rapport mensuel d'engagement par équipe"],
];
export function CompareTable({ current }) {
  return (
    <section className="mx-auto max-w-5xl px-5 py-16" data-testid="compare-table">
      <p className="p-eyebrow">Communities ou Business ?</p>
      <h2 className="mt-2 font-display text-4xl uppercase leading-[0.95] sm:text-5xl">Deux offres, deux usages.</h2>
      <div className="p-surface mt-8 overflow-x-auto">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead><tr className="border-b p-line"><th className="p-4" />{["Communities", "Business"].map((h) => (
            <th key={h} className={`p-4 text-base font-bold ${h.toLowerCase() === current ? "bg-[#B7F52A] text-[#080C0D]" : ""}`}>{h}</th>))}</tr></thead>
          <tbody>{ROWS.map(([k, a, b]) => (
            <tr key={k} className="border-b p-line last:border-0"><th className="p-4 font-bold">{k}</th><td className="p-4 p-t2">{a}</td><td className="p-4 p-t2">{b}</td></tr>))}</tbody>
        </table>
      </div>
    </section>
  );
}

export function MailContact({ title, text, subject, testId, vous }) {
  return (
    <section id="contact" className="mx-auto flex max-w-6xl scroll-mt-24 flex-col items-start gap-5 px-5 py-16 md:flex-row md:items-center md:justify-between" data-testid={testId}>
      <div><p className="font-display text-4xl uppercase">{title}</p><p className="mt-1 max-w-xl text-sm p-t2">{text}</p>
        <p className="mt-2 text-xs p-t3">{vous ? "Ou écrivez-nous directement" : "Ou écris-nous directement"} : <a href={mailto(subject)} className="font-bold underline" data-testid={`${testId}-email`}>{MAIL}</a></p></div>
      <a href={mailto(subject)} className="p-btn shrink-0" data-testid={`${testId}-btn`}><Mail size={16} /> Contactez-nous</a>
    </section>
  );
}
